import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate
} from '@tanstack/react-router';
import { moduleRegistry, RequireModule } from '@ddone/auth';
import { Button, EmptyState, ErrorState } from '@ddone/ui';
import { LockKeyhole, MapPinOff } from 'lucide-react';
import { lazy, Suspense, useSyncExternalStore, type ComponentType } from 'react';
import { AppShell } from './shell/app-shell';
import { AcceptInvitationPage } from './shell/accept-invitation-screen';

const DashboardPage = lazy(() =>
  import('./pages/dashboard-page').then((module) => ({ default: module.DashboardPage }))
);
const ClientsPage = lazy(() =>
  import('./pages/clients-page').then((module) => ({ default: module.ClientsPage }))
);
const CrmPage = lazy(() =>
  import('./pages/crm-page').then((module) => ({ default: module.CrmPage }))
);
const ProjectsPage = lazy(() =>
  import('./pages/projects-page').then((module) => ({ default: module.ProjectsPage }))
);
const NotesPage = lazy(() =>
  import('./pages/notes-page').then((module) => ({ default: module.NotesPage }))
);
const TasksPage = lazy(() =>
  import('./pages/tasks-page').then((module) => ({ default: module.TasksPage }))
);
const TimeTrackingPage = lazy(() =>
  import('./pages/time-tracking-page').then((module) => ({ default: module.TimeTrackingPage }))
);
const BillingPage = lazy(() =>
  import('./pages/billing-page').then((module) => ({ default: module.BillingPage }))
);
const ExpensesPage = lazy(() =>
  import('./pages/expenses-page').then((module) => ({ default: module.ExpensesPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/analytics-page').then((module) => ({ default: module.AnalyticsPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/settings-page').then((module) => ({ default: module.SettingsPage }))
);
const AccessPage = lazy(() =>
  import('./pages/access-page').then((module) => ({ default: module.AccessPage }))
);
const NotificationsPage = lazy(() =>
  import('./pages/notifications-page').then((module) => ({ default: module.NotificationsPage }))
);
const HelpPage = lazy(() =>
  import('./pages/help-page').then((module) => ({ default: module.HelpPage }))
);
const WorkspaceAdminPage = lazy(() =>
  import('./pages/workspace-admin-page').then((module) => ({ default: module.WorkspaceAdminPage }))
);

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: NotFoundPage,
  errorComponent: ({ error, reset }) => (
    <ErrorState
      title="La pagina ha riscontrato un errore"
      message={error.message}
      onRetry={reset}
    />
  )
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <LazyPage component={DashboardPage} />
});

function moduleRoute<const TPath extends string>(
  path: TPath,
  moduleId: string,
  permission: string,
  Component: ComponentType
) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => (
      <RequireModule
        moduleId={moduleId}
        permission={permission}
        fallback={
          <EmptyState
            icon={<LockKeyhole />}
            title="Modulo non disponibile"
            description="Il modulo non è abilitato per questo workspace oppure non disponi del permesso necessario."
          />
        }
      >
        <LazyPage component={Component} />
      </RequireModule>
    )
  });
}

const clientsRoute = moduleRoute('/clients', 'clients', 'clients.client.read', ClientsPage);
const crmRoute = moduleRoute('/crm', 'crm', 'crm.opportunity.read', CrmPage);
const projectsRoute = moduleRoute('/projects', 'projects', 'projects.project.read', ProjectsPage);
const notesRoute = moduleRoute('/notes', 'notes', 'notes.document.read', NotesPage);
const tasksRoute = moduleRoute('/tasks', 'tasks', 'tasks.task.read', TasksPage);
const timeRoute = moduleRoute('/time', 'time-tracking', 'time.entry.read', TimeTrackingPage);
const billingRoute = moduleRoute('/billing', 'billing', 'billing.item.read', BillingPage);
const expensesRoute = moduleRoute('/expenses', 'expenses', 'expenses.expense.read', ExpensesPage);
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: () => <LazyPage component={AnalyticsPage} />
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => <LazyPage component={SettingsPage} />
});
const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/access',
  component: () => <LazyPage component={AccessPage} />
});
const workspacesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/workspaces',
  component: () => <LazyPage component={WorkspaceAdminPage} />
});
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: () => <LazyPage component={NotificationsPage} />
});
const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: () => <LazyPage component={HelpPage} />
});
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitationPage
});
const pluginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plugins/$pluginId',
  component: PluginModulePage
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  clientsRoute,
  crmRoute,
  projectsRoute,
  notesRoute,
  tasksRoute,
  timeRoute,
  billingRoute,
  expensesRoute,
  analyticsRoute,
  settingsRoute,
  accessRoute,
  workspacesRoute,
  notificationsRoute,
  helpRoute,
  acceptInvitationRoute,
  pluginRoute
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  scrollRestoration: true
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={<MapPinOff />}
      title="Pagina non trovata"
      description="L’indirizzo non esiste o il modulo non è più accessibile."
      action={<Button onClick={() => void navigate({ to: '/' })}>Torna alla panoramica</Button>}
    />
  );
}

function LazyPage({ component: Component }: { component: ComponentType }) {
  return (
    <Suspense
      fallback={
        <div className="dd-table-loading" role="status">
          Caricamento pagina…
        </div>
      }
    >
      <Component />
    </Suspense>
  );
}

function PluginModulePage() {
  const { pluginId } = pluginRoute.useParams();
  const modules = useSyncExternalStore(
    (listener) => moduleRegistry.subscribe(listener),
    moduleRegistry.snapshot
  );
  const module = modules.find((candidate) => candidate.id === pluginId);
  const route = module?.routes?.[0];
  if (!module || !route) {
    return (
      <EmptyState
        icon={<MapPinOff />}
        title="Pagina plugin non disponibile"
        description="Il bundle non ha registrato una pagina oppure il modulo non è più attivo."
      />
    );
  }
  const Component = route.component;
  return <Component />;
}
